import { Plus, Trash2, ShoppingBag } from "lucide-react";
import StepShell from "./StepShell";
import { fmtMoney, siteConfig } from "../../config";

/**
 * Step 6 — Accessories.
 *
 * Customer can add any number of accessories. Each one captures:
 *   • name / description
 *   • part number
 *   • amount
 * The amount total is added to the deal calc in later steps. Lead payload
 * preserves the full list with name + part_number + amount.
 */
export default function AccessoriesStep({ byod, onContinue, onBack, firstName }) {
  const list = byod.state.accessories || [];

  const addRow = () => {
    byod.replaceSection("accessories", [
      ...list,
      { id: Date.now() + Math.random(), name: "", part_number: "", amount: "" },
    ]);
  };

  const update = (id, patch) => {
    byod.replaceSection(
      "accessories",
      list.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  };

  const remove = (id) => {
    byod.replaceSection("accessories", list.filter((row) => row.id !== id));
  };

  const total = byod.accessoriesTotal;
  const hasAny = list.length > 0;
  const canContinue =
    !hasAny ||
    list.every((row) => String(row.name || "").trim() && Number(row.amount || 0) > 0);

  return (
    <StepShell
      title={firstName ? `${firstName}, want to add any accessories to your deal?` : "Want to add any accessories to your deal?"}
      subtitle="Bedliners, all-weather mats, hitches, splash guards, towing packages — you name it. Add as many as you want."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!canContinue}
      testIdPrefix="byod-accessories-step"
    >
      <div className="rounded-2xl border-2 border-[var(--ssc-blue)]/30 bg-[var(--ssc-paper)] p-4 mb-4" data-testid="byod-accessory-site-cta">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)] grid place-items-center shrink-0">
            <ShoppingBag size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-extrabold text-[var(--ssc-navy)]">Browse the official Chevrolet accessories catalog</div>
            <p className="text-xs text-[var(--ssc-ink-2)] mt-0.5">
              Find the exact part numbers + prices for floor liners, hitches, racks, lighting,
              and more. Bring them back here and add them below.
            </p>
          </div>
          <a
            href={siteConfig.links.accessories}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => byod.replaceSection("accessory_site_interest", true)}
            className="ssc-btn ssc-btn-primary !py-2 !text-xs"
            data-testid="byod-accessory-site-link"
          >
            <ShoppingBag size={14} /> Shop Chevrolet Accessories
          </a>
        </div>
      </div>

      {!hasAny && (
        <div className="rounded-2xl border-2 border-[var(--ssc-line)] bg-white p-5 text-center" data-testid="byod-accessories-empty">
          <p className="text-[var(--ssc-ink-2)]">No accessories added yet.</p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <button
              type="button"
              onClick={addRow}
              className="ssc-btn ssc-btn-primary"
              data-testid="byod-accessory-add"
            >
              <Plus size={16} /> Add accessory
            </button>
            <button
              type="button"
              onClick={onContinue}
              className="ssc-btn ssc-btn-outline"
              data-testid="byod-accessory-none"
            >
              No accessories
            </button>
          </div>
        </div>
      )}

      {hasAny && (
        <div className="space-y-3" data-testid="byod-accessories-list">
          {list.map((row, idx) => (
            <div
              key={row.id}
              className="rounded-2xl border-2 border-[var(--ssc-line)] bg-white p-4"
              data-testid={`byod-accessory-row-${idx}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">
                  Accessory #{idx + 1}
                </div>
                <button
                  type="button"
                  onClick={() => remove(row.id)}
                  className="text-xs font-bold text-[var(--ssc-red)] hover:underline flex items-center gap-1"
                  data-testid={`byod-accessory-remove-${idx}`}
                >
                  <Trash2 size={12} /> Remove
                </button>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <label className="block sm:col-span-2">
                  <span className="ssc-label">Name or description</span>
                  <input
                    className="ssc-input"
                    value={row.name || ""}
                    onChange={(e) => update(row.id, { name: e.target.value })}
                    placeholder="e.g. All-Weather Floor Liners"
                    data-testid={`byod-accessory-name-${idx}`}
                  />
                </label>
                <label className="block">
                  <span className="ssc-label">Part number</span>
                  <input
                    className="ssc-input"
                    value={row.part_number || ""}
                    onChange={(e) => update(row.id, { part_number: e.target.value })}
                    placeholder="optional"
                    data-testid={`byod-accessory-part-${idx}`}
                  />
                </label>
                <label className="block">
                  <span className="ssc-label">Amount</span>
                  <input
                    className="ssc-input"
                    value={row.amount || ""}
                    onChange={(e) => update(row.id, { amount: e.target.value.replace(/[^0-9.]/g, "") })}
                    inputMode="decimal"
                    placeholder="$"
                    data-testid={`byod-accessory-amount-${idx}`}
                  />
                </label>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <button
              type="button"
              onClick={addRow}
              className="ssc-btn ssc-btn-outline"
              data-testid="byod-accessory-add-more"
            >
              <Plus size={14} /> Add another
            </button>
            {total > 0 && (
              <div className="text-sm" data-testid="byod-accessories-total">
                Accessory subtotal: <strong className="text-[var(--ssc-navy)]">{fmtMoney(total)}</strong>
              </div>
            )}
          </div>
        </div>
      )}
    </StepShell>
  );
}
